/**
 * Tech Tool — Manufacturer Lines in the Header Search Dropdown
 * File: assets/tt-brand-predictive.js
 *
 * EMPTY STATE (first click into search, before typing)
 *   The theme's recommendations are all Oemeta products and Oemeta keywords,
 *   which reads as though Oemeta is all we sell. This adds a full-width row of
 *   the eight product categories across the top, each deep-linking to that
 *   filter on the line card, plus the total line count. Categories rather than
 *   logos: finite, stable, already a real taxonomy, no usage rights to chase.
 *   Per-category counts are deliberately NOT shown — Fluids is a depth play and
 *   "Fluids 5" beside "Cutting Tools 122" would undersell it.
 *
 *   MOUNT POINT: .search__recommendations is itself a flex row on desktop, so
 *   injecting inside it turns the browse row into a sibling column and squeezes
 *   the product grid. It is inserted BEFORE that element, inside
 *   .search__content, which stacks as a column.
 *
 * TYPED QUERY
 *   Mounts into the PredictiveSearchResults container. Shopify's predictive
 *   endpoint only knows products, pages, articles and collections, so a brand
 *   name returns "No results found" even when we carry that line.
 *
 * USE THE THEME'S COMPONENTS, DO NOT RE-STYLE THEM.
 *   Headings render as <h4 class="h4">. Buttons render as
 *   <a class="btn btn--outline btn--small"> with the label inside
 *   <span class="btn__text">. That span is required: the theme paints its
 *   hover fill with a ::before that animates width 0 -> 100% from
 *   inset-inline-start, and .btn__text carries z-index:1 so the label stays
 *   above the fill. Text links carry .tt-link-underline (see
 *   snippets/tt-link-underline.liquid) for the hover colour and line wipe.
 *
 * The theme replaces the results container's innerHTML on every fetch, which
 * wipes anything we add there, so a MutationObserver re-injects after each
 * render while guarding against observing our own writes.
 *
 * Shares the sessionStorage cache key with assets/tt-brand-search.js so the
 * line card is fetched once per session, not once per surface.
 */

(function () {
  'use strict';

  var SOURCE_PATH = '/pages/linecard';
  var SECTION_ID = 'lc_directory';
  var CACHE_KEY = 'tt-brand-index-v1';
  var CACHE_TTL_MS = 1000 * 60 * 30;
  var MAX_RESULTS = 5;
  var MIN_QUERY = 2;

  var CATEGORY_LABELS = {
    'cutting-tools': 'Cutting Tools',
    'abrasives': 'Abrasives',
    'fluids': 'Fluids',
    'workholding': 'Workholding',
    'machine-accessories': 'Machine Accessories',
    'metrology': 'Metrology',
    'mro': 'MRO',
    'coolant-systems': 'Coolant Systems'
  };

  var CATEGORY_ORDER = [
    'cutting-tools', 'abrasives', 'fluids', 'workholding',
    'machine-accessories', 'metrology', 'mro', 'coolant-systems'
  ];

  var indexPromise = null;

  function slugify(name) {
    return String(name).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function categoryHref(cat) {
    return SOURCE_PATH + '?category=' + encodeURIComponent(cat) + '#lc-directory';
  }

  function parseRows(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var rows = doc.querySelectorAll('.lc-dir-row');
    var out = [];
    Array.prototype.forEach.call(rows, function (row) {
      var link = row.querySelector('.lc-dir-row__brand-link');
      var descEl = row.querySelector('.lc-dir-row__desc');
      if (!link) return;
      var name = (link.textContent || '').replace(/\s+/g, ' ').trim();
      if (!name) return;
      out.push({
        name: name,
        slug: slugify(name),
        desc: descEl ? (descEl.textContent || '').replace(/\s+/g, ' ').trim() : '',
        cat: row.getAttribute('data-lc-cat') || '',
        swiss: row.hasAttribute('data-lc-swiss')
      });
    });
    return out;
  }

  function readCache() {
    try {
      var parsed = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (!parsed || !parsed.at || !Array.isArray(parsed.rows)) return null;
      if (Date.now() - parsed.at > CACHE_TTL_MS) return null;
      return parsed.rows;
    } catch (e) { return null; }
  }

  function writeCache(rows) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), rows: rows }));
    } catch (e) {}
  }

  function loadIndex() {
    if (indexPromise) return indexPromise;
    var cached = readCache();
    if (cached) {
      indexPromise = Promise.resolve(cached);
      return indexPromise;
    }
    indexPromise = fetch(SOURCE_PATH + '?sections=' + SECTION_ID, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('section fetch failed');
        return r.json();
      })
      .then(function (json) {
        var html = json && json[SECTION_ID];
        if (!html) throw new Error('section missing');
        return parseRows(html);
      })
      .catch(function () {
        return fetch(SOURCE_PATH, { credentials: 'same-origin' })
          .then(function (r) { return r.text(); })
          .then(parseRows);
      })
      .then(function (rows) {
        if (rows && rows.length) writeCache(rows);
        return rows || [];
      })
      .catch(function () { return []; });
    return indexPromise;
  }

  function scoreRow(row, q) {
    var name = row.name.toLowerCase();
    var desc = row.desc.toLowerCase();
    if (name === q) return 100;
    if (name.indexOf(q) === 0) return 80;
    if (name.indexOf(q) !== -1) return 60;
    if (new RegExp('\\b' + q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).test(desc)) return 30;
    if (desc.indexOf(q) !== -1) return 15;
    return 0;
  }

  function search(rows, query) {
    var q = query.toLowerCase().trim();
    if (q.length < MIN_QUERY) return [];
    return rows
      .map(function (r) { return { row: r, score: scoreRow(r, q) }; })
      .filter(function (x) { return x.score > 0; })
      .sort(function (a, b) {
        if (b.score !== a.score) return b.score - a.score;
        return a.row.name.localeCompare(b.row.name);
      })
      .map(function (x) { return x.row; });
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  /* Theme button. The .btn__text span is load-bearing — see the note above. */
  function themeButton(cls, href, label, ariaLabel) {
    var a = el('a', cls);
    a.href = href;
    a.appendChild(el('span', 'btn__text', label));
    if (ariaLabel) a.setAttribute('aria-label', ariaLabel);
    return a;
  }

  function heading() {
    return el('h4', 'h4 tt-ps__heading', 'Manufacturer lines');
  }

  function syncInset(container, block) {
    var ref = container.querySelector(
      '.predictive-search-result__heading, [class*="predictive-search-result"]'
    );
    if (!ref || block.contains(ref)) return false;
    var cr = container.getBoundingClientRect();
    var rr = ref.getBoundingClientRect();
    if (!cr.width || !rr.width) return false;
    var right = Math.round(cr.right - rr.right);
    var left = Math.round(rr.left - cr.left);
    if (right > 0 && right < 200) block.style.paddingRight = right + 'px';
    if (left > 0 && left < 200) block.style.paddingLeft = left + 'px';
    return true;
  }

  function scheduleInset(container, block) {
    var tries = 0;
    (function attempt() {
      if (!block.isConnected) return;
      if (syncInset(container, block)) return;
      if (++tries < 10) window.setTimeout(attempt, 80);
    })();
  }

  function buildBrowse(rows) {
    var wrap = el('div', 'tt-ps--browse');
    wrap.setAttribute('data-tt-ps-browse', '');

    wrap.appendChild(heading());

    var chips = el('div', 'tt-ps-chips');
    var present = {};
    rows.forEach(function (r) { if (r.cat) present[r.cat] = true; });

    CATEGORY_ORDER.forEach(function (cat) {
      if (rows.length && !present[cat]) return;
      chips.appendChild(themeButton(
        'tt-ps-chip btn btn--outline btn--small',
        categoryHref(cat),
        CATEGORY_LABELS[cat],
        'Browse ' + CATEGORY_LABELS[cat] + ' manufacturer lines'
      ));
    });
    wrap.appendChild(chips);

    var note = el('p', 'tt-ps-note');
    var total = rows.length || 228;
    note.appendChild(document.createTextNode(
      total + ' manufacturer lines. Thousands more on request. '
    ));
    var all = el('a', 'tt-ps-note__link tt-link-underline', 'View the full line card');
    all.href = SOURCE_PATH;
    note.appendChild(all);
    wrap.appendChild(note);

    return wrap;
  }

  function buildResults(results, query) {
    var wrap = el('div', 'tt-ps');
    wrap.setAttribute('data-tt-ps', '');
    wrap.setAttribute('data-tt-ps-query', query);

    wrap.appendChild(heading());

    var list = el('ul', 'tt-ps__list');
    list.setAttribute('role', 'list');

    results.slice(0, MAX_RESULTS).forEach(function (row) {
      var li = el('li', 'tt-ps__item');

      var a = el('a', 'tt-ps__main');
      a.href = SOURCE_PATH + '#lc-brand-' + row.slug;
      a.setAttribute('aria-label', 'View ' + row.name + ' on the line card');

      var nameLine = el('span', 'tt-ps__nameline');
      nameLine.appendChild(el('span', 'tt-ps__name tt-link-underline', row.name));
      if (row.swiss) nameLine.appendChild(el('span', 'tt-ps__tag', 'SWISS'));
      a.appendChild(nameLine);

      if (row.desc) a.appendChild(el('span', 'tt-ps__desc', row.desc));
      li.appendChild(a);

      var catLabel = CATEGORY_LABELS[row.cat];
      if (catLabel) {
        var cat = el('a', 'tt-ps__cat', catLabel);
        cat.href = categoryHref(row.cat);
        cat.setAttribute('aria-label', 'Browse all ' + catLabel + ' lines');
        li.appendChild(cat);
      }

      li.appendChild(themeButton(
        'tt-ps__quote btn btn--outline btn--small',
        '/pages/contact?type=quote&manufacturer=' + encodeURIComponent(row.name),
        'Quote',
        'Request a quote for ' + row.name
      ));

      list.appendChild(li);
    });

    wrap.appendChild(list);

    if (results.length > MAX_RESULTS) {
      // Short label: the theme capitalizes button text, and a long phrase in
      // Title Case reads badly.
      wrap.appendChild(themeButton(
        'tt-ps__all btn btn--link',
        '/search?q=' + encodeURIComponent(query),
        'See all ' + results.length + ' lines',
        'See all ' + results.length + ' matching manufacturer lines'
      ));
    }

    return wrap;
  }

  function attach(host) {
    var input = host.querySelector('input[type="search"]');
    var container = host.querySelector('[id^="PredictiveSearchResults-"]');
    if (!input || !container) return;
    if (host.hasAttribute('data-tt-ps-bound')) return;
    host.setAttribute('data-tt-ps-bound', '');

    var recs = host.querySelector('.search__recommendations');
    var browseHome = (recs && recs.parentElement) ? recs.parentElement : container;
    var browseAnchor = (recs && recs.parentElement) ? recs : browseHome.firstChild;

    var observer = null;
    var injecting = false;

    function restoreEmpty() {
      var empty = container.querySelector('.empty-state[data-tt-ps-hidden]');
      if (empty) {
        empty.style.removeProperty('display');
        empty.removeAttribute('data-tt-ps-hidden');
      }
    }

    function clearBrowse() {
      var b = browseHome.querySelector('[data-tt-ps-browse]');
      if (b) b.remove();
    }

    function showBrowse(rows) {
      if (browseHome.querySelector('[data-tt-ps-browse]')) return;
      browseHome.insertBefore(buildBrowse(rows), browseAnchor);
    }

    function placeResults(block) {
      injecting = true;
      if (observer) observer.disconnect();

      var existing = container.querySelector('[data-tt-ps]');
      if (existing) existing.remove();

      if (block) {
        container.insertBefore(block, container.firstChild);
        scheduleInset(container, block);
      }

      if (observer) observer.observe(container, { childList: true, subtree: false });
      injecting = false;
    }

    function update() {
      var query = (input.value || '').trim();

      if (query.length < MIN_QUERY) {
        placeResults(null);
        restoreEmpty();
        loadIndex().then(function (rows) {
          if ((input.value || '').trim().length >= MIN_QUERY) return;
          showBrowse(rows);
        });
        return;
      }

      clearBrowse();

      loadIndex().then(function (rows) {
        if ((input.value || '').trim() !== query) return;
        var results = search(rows, query);
        if (!results.length) {
          placeResults(null);
          restoreEmpty();
          return;
        }
        placeResults(buildResults(results, query));
        var empty = container.querySelector('.empty-state');
        if (empty && !empty.hasAttribute('data-tt-ps-hidden')) {
          empty.style.display = 'none';
          empty.setAttribute('data-tt-ps-hidden', '');
        }
      });
    }

    observer = new MutationObserver(function () {
      if (injecting) return;
      var query = (input.value || '').trim();
      if (query.length < MIN_QUERY) { update(); return; }
      var existing = container.querySelector('[data-tt-ps]');
      if (!existing || existing.getAttribute('data-tt-ps-query') !== query) {
        update();
      } else {
        scheduleInset(container, existing);
      }
    });
    observer.observe(container, { childList: true, subtree: false });

    input.addEventListener('input', function () {
      window.clearTimeout(input._ttPsTimer);
      input._ttPsTimer = window.setTimeout(update, 180);
    });
    input.addEventListener('focus', update);

    window.addEventListener('resize', function () {
      var block = container.querySelector('[data-tt-ps]');
      if (block) {
        block.style.removeProperty('padding-right');
        block.style.removeProperty('padding-left');
        scheduleInset(container, block);
      }
    });
  }

  function init() {
    document.querySelectorAll('predictive-search').forEach(attach);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
